import {
    BadgeColor,
    BadgeShape,
    BadgeSize,
    ButtonFlavor,
    ButtonSize,
    ButtonType,
    ExBadge,
    ExButton,
    ExIconButton,
    ExInput,
    ExMenuItem,
    ExSelect,
    ExToggle,
    IconButtonFlavor,
    IconButtonSize,
    IconButtonType,
    MenuItemVariant,
    SelectType,
} from '@boomi/exosphere';
import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import {
    fetchIdentityProviders,
    type IdentityProvider,
} from '../identity-providers/identityProviders';
import { trackConnectorEnableDisable } from '../pendo';
import { GENERIC_ERROR_MESSAGE } from '../shared/apiUtils';
import ErrorAlertBanner from '../shared/ErrorAlertBanner';
import styles from './ConnectionTitleBar.module.css';
import { type Connection, deleteConnection, updateConnection } from './connections';

interface ConnectionTitleBarProps {
    connection: Connection;
    connectorName: string;
    onClose: () => void;
}

enum stateMode {
    VIEW = 'view',
    EDIT = 'edit',
    DELETE = 'delete',
}

const ConnectionTitleBar = ({ connection, connectorName, onClose }: ConnectionTitleBarProps) => {
    const { appId, identityProviderName, name, identityProviderId, enabled } = connection;
    const connectionData = {
        name,
        identityProviderId,
        enabled,
        options: connection.options,
        disabledTools: connection.disabledTools,
    };

    const [mode, setMode] = useState(stateMode.VIEW);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [connectionName, setConnectionName] = useState(name);
    const [connectionIdp, setConnectionIdp] = useState(identityProviderId);
    const [loadingIdentityProviders, setLoadingIdentityProviders] = useState(false);
    const [identityProviders, setIdentityProviders] = useState<IdentityProvider[]>([]);

    useEffect(() => {
        const fetchIdps = async () => {
            setLoadingIdentityProviders(true);
            try {
                const idps = (await fetchIdentityProviders()).results;
                setIdentityProviders(idps);
            } catch (err) {
                setError(err instanceof Error ? err.message : GENERIC_ERROR_MESSAGE);
            } finally {
                setLoadingIdentityProviders(false);
            }
        };

        fetchIdps();
    }, []);

    const setModeAnimated = (next: stateMode) => {
        if (!document.startViewTransition) {
            setMode(next);
            return;
        }
        document.startViewTransition(() => {
            flushSync(() => setMode(next));
        });
    };

    const getIdpNameById = (id: string) =>
        identityProviders.find((idp) => idp.id === id)?.name ?? 'Unknown';

    return (
        <section className={styles.titleBarContainer}>
            {error && (
                <ErrorAlertBanner className={styles.errorBanner} onClose={() => setError(null)}>
                    {error}
                </ErrorAlertBanner>
            )}
            <div className={styles.titleBar}>
                {mode === stateMode.VIEW && (
                    <>
                        <ExIconButton
                            data-pendo-button="config-view-back"
                            className={styles.backButton}
                            icon="Left arrow"
                            label="Back"
                            type={IconButtonType.SECONDARY}
                            flavor={IconButtonFlavor.BRANDED}
                            size={IconButtonSize.SMALL}
                            onClick={onClose}
                        />
                        <span className={styles.name}>{connectionName}</span>
                        <ExBadge
                            className={styles.idpName}
                            color={BadgeColor.BLUE}
                            shape={BadgeShape.ROUND}
                            size={BadgeSize.SMALL}
                        >
                            {getIdpNameById(connectionIdp)}
                        </ExBadge>
                        <ExIconButton
                            data-pendo-button="config-view-edit"
                            className={styles.editButton}
                            icon="Edit"
                            type={IconButtonType.TERTIARY}
                            flavor={IconButtonFlavor.BRANDED}
                            size={IconButtonSize.SMALL}
                            onClick={() => setModeAnimated(stateMode.EDIT)}
                        />
                        <ExToggle
                            data-pendo-toggle="config-view-status"
                            className={styles.statusToggle}
                            on={enabled}
                            toggleLabel="Enabled"
                            showToggleLabel
                            onChange={async (e) => {
                                const enabled = (e as unknown as CustomEvent<boolean>).detail;
                                trackConnectorEnableDisable({
                                    connector_name: connectorName,
                                    connector_current_status: enabled ? 'enabled' : 'disabled',
                                });
                                try {
                                    await updateConnection(appId, {
                                        ...connectionData,
                                        enabled,
                                    });
                                } catch (err) {
                                    setError(
                                        err instanceof Error ? err.message : GENERIC_ERROR_MESSAGE,
                                    );
                                }
                            }}
                        />
                        <ExIconButton
                            data-pendo-button="config-view-delete"
                            className={styles.deleteButton}
                            icon="Delete"
                            type={IconButtonType.TERTIARY}
                            flavor={IconButtonFlavor.BRANDED}
                            size={IconButtonSize.SMALL}
                            onClick={() => setModeAnimated(stateMode.DELETE)}
                        />
                    </>
                )}

                {mode === stateMode.EDIT && (
                    <>
                        <ExInput
                            className={styles.nameInput}
                            type="text"
                            value={connectionName}
                            onInput={(e) => {
                                const name = (e.target as HTMLInputElement).value;
                                setConnectionName(name);
                            }}
                            placeholder={`e.g. My ${identityProviderName} Config`}
                            invalid={hasSubmitted && connectionName.trim() === ''}
                            required
                            suppressValidityCheck
                        />
                        <ExSelect
                            className={styles.idpSelect}
                            type={SelectType.SINGLE}
                            selected={connectionIdp}
                            onChange={(e) => {
                                const idp = (e as CustomEvent<{ value: string }>).detail.value;
                                setConnectionIdp(idp);
                            }}
                            placeholder={
                                loadingIdentityProviders
                                    ? 'Loading...'
                                    : identityProviders.length === 0
                                      ? 'No identity providers available'
                                      : 'Select an identity provider…'
                            }
                            invalid={hasSubmitted && !connectionIdp}
                            required
                            valueBasedSelection
                            showSelectedLabel
                            hideClearIcon
                        >
                            {identityProviders.map((idp) => (
                                <ExMenuItem
                                    key={idp.id}
                                    value={idp.id}
                                    variant={MenuItemVariant.OPTION}
                                >
                                    {idp.name}
                                </ExMenuItem>
                            ))}
                        </ExSelect>
                        <div className={styles.actionButtons}>
                            <ExButton
                                data-pendo-button="config-edit-cancel"
                                type={ButtonType.SECONDARY}
                                flavor={ButtonFlavor.BRANDED}
                                size={ButtonSize.SMALL}
                                onClick={() => {
                                    // reset to original values
                                    setConnectionName(name);
                                    setConnectionIdp(identityProviderId);
                                    setModeAnimated(stateMode.VIEW);
                                }}
                            >
                                Cancel
                            </ExButton>
                            <ExButton
                                data-pendo-button="config-edit-save"
                                type={ButtonType.SECONDARY}
                                flavor={ButtonFlavor.BRANDED}
                                size={ButtonSize.SMALL}
                                disabled={isSubmitting}
                                onClick={async () => {
                                    setHasSubmitted(true);
                                    if (connectionName.trim() === '') return;
                                    if (!connectionIdp) return;

                                    setIsSubmitting(true);

                                    try {
                                        await updateConnection(connection.appId, {
                                            ...connectionData,
                                            name: connectionName,
                                            identityProviderId: connectionIdp,
                                        });
                                    } catch (err) {
                                        setError(
                                            err instanceof Error
                                                ? err.message
                                                : GENERIC_ERROR_MESSAGE,
                                        );
                                    } finally {
                                        setHasSubmitted(false);
                                        setIsSubmitting(false);
                                        setModeAnimated(stateMode.VIEW);
                                    }
                                }}
                            >
                                {isSubmitting ? 'Saving...' : 'Done'}
                            </ExButton>
                        </div>
                    </>
                )}

                {mode === stateMode.DELETE && (
                    <>
                        <p className={styles.deleteMessage}>Delete this config?</p>
                        <div className={styles.actionButtons}>
                            <ExButton
                                data-pendo-button="config-delete-cancel"
                                type={ButtonType.SECONDARY}
                                flavor={ButtonFlavor.BRANDED}
                                size={ButtonSize.SMALL}
                                onClick={() => setModeAnimated(stateMode.VIEW)}
                            >
                                Cancel
                            </ExButton>
                            <ExButton
                                data-pendo-button="config-delete-confirm"
                                type={ButtonType.SECONDARY}
                                flavor={ButtonFlavor.RISKY}
                                size={ButtonSize.SMALL}
                                onClick={async () => {
                                    setIsDeleting(true);
                                    try {
                                        await deleteConnection(appId);
                                        onClose();
                                    } catch (err) {
                                        setError(
                                            err instanceof Error
                                                ? err.message
                                                : GENERIC_ERROR_MESSAGE,
                                        );
                                        setIsDeleting(false);
                                        setModeAnimated(stateMode.VIEW);
                                    }
                                }}
                            >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </ExButton>
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};

export default ConnectionTitleBar;
